/**
 * Dev scaffold for voice capture + session processing.
 *
 * This screen is the ground-truth test harness for the tauri-plugin-voice
 * bridge. Three independent test surfaces live here:
 *
 *   1. Short recording + playback (enrollment plumbing, Phase 2)
 *   2. Session recording + WhisperKit transcription (Phase 3a + 3d)
 *   3. Diarization against the user's voiceprint (Phase 3b + 3c)
 *
 * It is NOT the shipping conversation UI. Later commits replace this
 * once per-turn engine scoring + proper session storage land.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { invoke } from "@tauri-apps/api/core";
import {
  AlertTriangle,
  CheckCircle2,
  Headphones,
  Loader2,
  Mic,
  Play,
  RefreshCw,
  RotateCcw,
  Square,
  UserCheck,
} from "lucide-react";

import { Button } from "@/ui/Button";
import { Card, CardContent, CardHeader, CardTitle } from "@/ui/Card";
import { usePlatform } from "@/hooks/usePlatform";
import { useAudioInputRoute, type AudioInputRoute } from "@/hooks/useAudioInputRoute";
import { useContacts } from "@/hooks/useContacts";
import { useDeveloperMode } from "@/hooks/useDeveloperMode";
import { useLiveSpeakerLabel } from "@/hooks/useLiveSpeakerLabel";
import { useLiveTranscript } from "@/hooks/useLiveTranscript";
import { useVoiceRecording } from "@/hooks/useVoiceRecording";
import { useVoiceSession } from "@/hooks/useVoiceSession";
import { computeVoiceprint, getVoiceprint } from "@/hooks/useVoiceprint";
import { processSession } from "@/hooks/useSessionProcessor";
import { getFullName } from "@/lib/data";
import { clearProfile, loadProfile, saveProfile } from "@/onboarding/profileStorage";
import type {
  DiarizationResult,
  ProcessedSession,
  ProcessedTurn,
  ProvisionalSpeaker,
  VoiceSession,
  VoiceprintSummary,
} from "@/types/voice";
import type { Contact } from "@/lib/data";

import { WhoWasThisPrompt } from "./WhoWasThisPrompt";

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function formatTime(seconds: number): string {
  return `${seconds.toFixed(1)}s`;
}

export function Conversation() {
  const { haptic } = usePlatform();
  const devMode = useDeveloperMode();
  const { permission, requestPermission, error } = useVoiceRecording();

  const session = useVoiceSession();
  const { contacts, createAppleContact } = useContacts();
  const liveLabelSessionId = session.isRecording ? session.current?.sessionId ?? null : null;
  const liveLabel = useLiveSpeakerLabel(liveLabelSessionId);
  const liveTranscript = useLiveTranscript(liveLabelSessionId);
  const inputRoute = useAudioInputRoute(true);

  const [lastSession, setLastSession] = useState<VoiceSession | null>(null);
  const [diarization, setDiarization] = useState<DiarizationResult | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processError, setProcessError] = useState<string | null>(null);

  /** Phase 3f — full processed session incl. provisionals to resolve. */
  const [processed, setProcessed] = useState<ProcessedSession | null>(null);
  const [pendingProvisionals, setPendingProvisionals] = useState<
    ProvisionalSpeaker[] | null
  >(null);

  const [voiceprint, setVoiceprintStatus] = useState<VoiceprintSummary | null>(null);
  const [isRecomputing, setIsRecomputing] = useState(false);
  const [recomputeError, setRecomputeError] = useState<string | null>(null);
  const [resetArmed, setResetArmed] = useState(false);
  const resetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let cancelled = false;
    getVoiceprint("user_self").then((vp) => {
      if (!cancelled) setVoiceprintStatus(vp);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * "This was me" on a diarized transcript turn. Reassigns the whole
   * cluster (a cluster is one speaker, so the tap fixes every turn
   * that voice owns in this session) and folds the turn's audio slice
   * into user_self as a new sample. Updates the in-memory processed
   * state optimistically so the UI reflects the new attribution
   * without a full refetch.
   */
  const handleReassignTurnAsSelf = useCallback(
    async (turnId: string, clusterLocalId: number) => {
      haptic("light");
      try {
        await invoke<{ sampleCount: number; cosine: number }>(
          "plugin:voice|adapt_user_self_from_turn",
          { turnId }
        );
        setProcessed((prev) =>
          prev
            ? {
                ...prev,
                turns: prev.turns.map((t) =>
                  t.clusterLocalId === clusterLocalId
                    ? { ...t, contactId: "user_self", similarity: 1.0 }
                    : t
                ),
              }
            : prev
        );
        const vp = await getVoiceprint("user_self");
        setVoiceprintStatus(vp);
      } catch (err) {
        setProcessError(String(err));
      }
    },
    [haptic]
  );

  const handleSessionStart = async () => {
    haptic("medium");
    if (permission !== "granted") {
      const next = await requestPermission();
      if (next !== "granted") return;
    }
    setDiarization(null);
    setProcessError(null);
    await session.start();
  };

  const handleSessionStop = async () => {
    haptic("light");
    const result = await session.stop();
    if (result) setLastSession(result);
  };

  /**
   * Run the Phase 3f pipeline end-to-end on the last recorded session:
   * transcribe + diarize + library-match + persist. Surfaces any
   * unrecognized speakers via the "Who was this?" prompt so the user
   * can link them to their contacts.
   */
  const handleProcess = async () => {
    if (!lastSession) return;
    haptic("light");
    setProcessError(null);
    setIsProcessing(true);
    try {
      const result = await processSession({
        sessionId: lastSession.sessionId,
        audioPath: lastSession.path,
        startedAt: lastSession.startedAt,
        endedAt: lastSession.endedAt,
        durationSeconds: lastSession.durationSeconds,
      });
      setProcessed(result);

      // Keep the diagnostic card alive: it reads modelVersion +
      // fullSessionCosine from this fake-diarization summary. Per-turn
      // rendering now comes straight from processed.turns instead.
      setDiarization({
        segments: result.turns.map((t) => ({
          startSeconds: t.startSeconds,
          endSeconds: t.endSeconds,
          speaker: t.contactId === "user_self" ? "user" : "other",
          similarity: t.similarity ?? 0,
        })),
        modelVersion: result.modelVersion,
        voiceprintId: "user_self",
        fullSessionCosine: result.fullSessionCosine,
      });

      if (result.provisionalSpeakers.length > 0) {
        setPendingProvisionals(result.provisionalSpeakers);
      }
    } catch (err) {
      setProcessError(String(err));
    } finally {
      setIsProcessing(false);
    }
  };

  const voiceprintTrustworthy =
    diarization?.modelVersion === "ecapa-tdnn-v1";

  const voiceprintIsFallback =
    voiceprint?.modelVersion === "fallback-energy-band";

  /**
   * Wipe the stored profile and reload the app so first-run onboarding
   * fires again. Used when recompute fails because enrollment audio
   * files no longer exist (app reinstall wipes Documents).
   *
   * Two-tap pattern because window.confirm() does not work in Tauri
   * WKWebView — it silently returns undefined. First tap arms the
   * action with a 4-second timeout; second tap within that window
   * actually clears and reloads.
   */
  const handleResetEnrollment = () => {
    haptic("medium");
    if (!resetArmed) {
      setResetArmed(true);
      if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
      resetTimerRef.current = setTimeout(() => setResetArmed(false), 4000);
      return;
    }
    if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    clearProfile();
    window.location.reload();
  };

  useEffect(() => {
    return () => {
      if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    };
  }, []);

  /**
   * Re-run ECAPA embedding across the stored enrollment audio files
   * and overwrite the user_self voiceprint. Used to upgrade a fallback
   * voiceprint to the real one after a Core ML load fix, without
   * forcing the user through onboarding again.
   */
  const handleRecomputeVoiceprint = async () => {
    const profile = loadProfile();
    if (!profile || profile.answers.length === 0) {
      setRecomputeError("No enrollment answers found. You need to re-enroll.");
      return;
    }
    haptic("medium");
    setRecomputeError(null);
    setIsRecomputing(true);
    try {
      const paths = profile.answers.map((a) => a.audioPath);
      const summary = await computeVoiceprint(paths, "user_self");
      setVoiceprintStatus(summary);
      saveProfile({
        ...profile,
        voiceprint: {
          id: summary.id,
          dim: summary.dim,
          sampleCount: summary.sampleCount,
          enrolledAt: summary.enrolledAt,
        },
      });
    } catch (err) {
      setRecomputeError(String(err));
    } finally {
      setIsRecomputing(false);
    }
  };

  return (
    <div className="flex flex-col gap-6 p-6">
      {permission === "denied" && (
        <p className="text-center text-sm text-destructive">
          Microphone access is denied. Enable it in Settings → Meridian.
        </p>
      )}

      {error && (
        <Card className="border-destructive">
          <CardContent className="p-4 text-sm text-destructive">{error}</CardContent>
        </Card>
      )}

      {/* Voiceprint status — so the user can see if diarization will work */}
      <Card>
        <CardHeader>
          <CardTitle>Voiceprint</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-3 text-sm">
          {voiceprint ? (
            <>
              <div className="flex items-start gap-2">
                {voiceprintIsFallback ? (
                  <AlertTriangle className="h-4 w-4 shrink-0 text-destructive mt-0.5" />
                ) : (
                  <CheckCircle2 className="h-4 w-4 shrink-0 text-success mt-0.5" />
                )}
                <div className="flex flex-col gap-1">
                  <p>
                    Model: <code className="text-xs">{voiceprint.modelVersion}</code>
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {voiceprint.dim}-dim · {voiceprint.sampleCount} samples · enrolled{" "}
                    {new Date(voiceprint.enrolledAt).toLocaleDateString()}
                  </p>
                  {voiceprintIsFallback && (
                    <p className="pt-2 text-xs text-destructive">
                      Fallback embedding. Real ECAPA model didn&rsquo;t load at enrollment time.
                      Tap below to recompute using your existing enrollment audio once the Core ML
                      bundle is fixed.
                    </p>
                  )}
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <Button
                  variant="secondary"
                  onClick={handleRecomputeVoiceprint}
                  disabled={isRecomputing}
                >
                  {isRecomputing ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Recomputing...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="h-4 w-4" />
                      Recompute voiceprint
                    </>
                  )}
                </Button>
                <Button
                  variant="ghost"
                  onClick={handleResetEnrollment}
                  className={`text-xs ${
                    resetArmed ? "text-destructive" : "text-muted-foreground"
                  }`}
                >
                  <RotateCcw className="h-3 w-3" />
                  {resetArmed
                    ? "Tap again to confirm reset"
                    : "Reset enrollment and start over"}
                </Button>
              </div>
              {recomputeError && (
                <p className="text-xs text-destructive">{recomputeError}</p>
              )}
            </>
          ) : (
            <p className="text-muted-foreground italic">No voiceprint stored yet.</p>
          )}
        </CardContent>
      </Card>

      {/* Session test surface — Phase 3 */}
      <Card>
        <CardHeader>
          <CardTitle>Session + transcription + diarization (Phase 3)</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4 text-sm">
          <div className="flex items-center gap-3">
            {session.isRecording ? (
              <Button variant="destructive" onClick={handleSessionStop}>
                <Square className="h-4 w-4" fill="currentColor" />
                Stop session
              </Button>
            ) : (
              <Button
                onClick={handleSessionStart}
                disabled={permission === "denied"}
              >
                <Mic className="h-4 w-4" />
                Start session
              </Button>
            )}
            {session.isRecording && (
              <span className="font-mono text-base tabular-nums text-muted-foreground">
                {formatDuration(session.elapsedSeconds)}
              </span>
            )}
            {session.isRecording && (
              <LiveSpeakerChip label={liveLabel} contacts={contacts} />
            )}
          </div>

          {inputRoute && <InputRouteChip route={inputRoute} />}

          {session.isRecording && (
            <LiveCaption transcript={liveTranscript} />
          )}

          {lastSession && !session.isRecording && (
            <div className="flex flex-col gap-2 rounded-lg border border-border p-3">
              <Row label="Session ID" value={lastSession.sessionId.slice(0, 8) + "…"} />
              <Row
                label="Duration"
                value={formatDuration(Math.round(lastSession.durationSeconds))}
              />
              <Row label="Size" value={formatBytes(lastSession.sizeBytes)} />
              <div className="pt-2">
                <Button
                  onClick={handleProcess}
                  disabled={isProcessing}
                  className="w-full"
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    "Transcribe + Diarize"
                  )}
                </Button>
                {isProcessing && (
                  <p className="pt-2 text-xs text-muted-foreground">
                    First session downloads the on-device speech model
                    (~200&nbsp;MB) and the language pack. After this, processing
                    finishes in a couple of seconds.
                  </p>
                )}
              </div>
            </div>
          )}

          {processError && (
            <p className="text-sm text-destructive">{processError}</p>
          )}

          {devMode && processed && (
            <div className="flex items-center justify-center gap-4 rounded-lg border border-yellow-400/30 bg-yellow-400/5 px-4 py-2 font-mono text-[11px] text-yellow-200">
              <DevResultSegment
                value={processed.interactions.length}
                label="recognized"
              />
              <span className="text-yellow-400/30">·</span>
              <DevResultSegment
                value={processed.adaptationCount}
                label="learned"
              />
              <span className="text-yellow-400/30">·</span>
              <DevResultSegment
                value={processed.provisionalSpeakers.length}
                label="new"
              />
            </div>
          )}

          {diarization && !voiceprintTrustworthy && (
            <div className="flex items-start gap-2 rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-xs">
              <AlertTriangle className="h-4 w-4 shrink-0 text-destructive" />
              <p className="text-destructive">
                Voiceprint model version is <code>{diarization.modelVersion}</code>. Diarization won&rsquo;t
                discriminate speakers reliably until the real ECAPA model is loaded. Re-enroll after
                the Core ML bundle is fixed.
              </p>
            </div>
          )}

          {diarization && (
            <div className="rounded-lg border border-border bg-muted/20 p-3 text-xs">
              <p className="text-muted-foreground uppercase tracking-[0.2em]">Diagnostic</p>
              <p className="pt-1 font-mono">
                Full-session cosine vs voiceprint:{" "}
                <span className="font-semibold text-foreground">
                  {diarization.fullSessionCosine.toFixed(3)}
                </span>
              </p>
              <p className="pt-1 text-muted-foreground">
                {diarization.fullSessionCosine < 0.4
                  ? "LOW — voiceprint likely doesn't match this session. Bad enrollment or audio format mismatch."
                  : diarization.fullSessionCosine < 0.6
                    ? "Modest — some of your audio matches, but conditions differ from enrollment."
                    : "Good — voiceprint recognizes your voice reliably."}
              </p>
            </div>
          )}

          {processed && processed.turns.length > 0 && (
            <div className="flex flex-col gap-3">
              <p className="text-muted-foreground text-xs uppercase tracking-[0.2em]">
                Diarized transcript
              </p>
              <ul className="flex flex-col gap-3">
                {processed.turns.map((turn) => (
                  <TranscriptTurnRow
                    key={turn.id}
                    turn={turn}
                    contacts={contacts}
                    onReassignAsSelf={handleReassignTurnAsSelf}
                  />
                ))}
              </ul>
            </div>
          )}

          {processed && processed.turns.length === 0 && (
            <p className="text-sm text-muted-foreground italic">
              Transcription produced no segments. Try a longer recording.
            </p>
          )}

          {diarization && diarization.segments.length === 0 && (
            <p className="text-sm text-muted-foreground italic">
              Diarization found no voice activity.
            </p>
          )}
        </CardContent>
      </Card>

      {pendingProvisionals && pendingProvisionals.length > 0 && (
        <WhoWasThisPrompt
          provisionals={pendingProvisionals}
          contacts={contacts}
          onCreateContact={async (firstName) => {
            // Minimal stub contact — first name only. We pass an empty
            // lastName and let Apple's create flow assign an identifier.
            // The create hook returns a fully-formed Contact with the
            // new appleContactId the resolution command needs.
            const now = new Date().toISOString();
            const stub: Contact = {
              id: `temp-${Date.now()}`,
              firstName,
              lastName: "",
              phones: [],
              emails: [],
              addresses: [],
              socials: [],
              jobs: [],
              education: [],
              skills: [],
              tags: [],
              status: "connected",
              notes: "",
              move: null,
              path: null,
              source: "manual",
              claimed: false,
              interactionCount: 0,
              createdAt: now,
              updatedAt: now,
              appleContactLinked: true,
            };
            return createAppleContact(stub);
          }}
          onAnyResolved={() => {
            // Could refetch processed session here; for now we just let
            // the remaining slots on-screen continue their resolution
            // cycle and dismiss when all are done.
          }}
          onDismiss={() => setPendingProvisionals(null)}
        />
      )}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium">{value}</span>
    </div>
  );
}

function LiveCaption({
  transcript,
}: {
  transcript: ReturnType<typeof useLiveTranscript>;
}) {
  const { finals, partial } = transcript;
  if (finals.length === 0 && !partial) return null;

  // Show the tail end of the transcript — last ~3 finals + the partial.
  // Older finals are accessible in the post-session view; the caption is
  // live context, not a scrollback log.
  const shownFinals = finals.slice(-3);

  return (
    <div className="rounded-lg border border-border/60 bg-muted/30 px-3 py-2 text-sm leading-relaxed">
      {shownFinals.map((seg, i) => (
        <span key={`final-${i}`} className="text-foreground">
          {seg.text}
          {" "}
        </span>
      ))}
      {partial && (
        <span className="text-muted-foreground italic">
          {partial.text}
        </span>
      )}
    </div>
  );
}

function LiveSpeakerChip({
  label,
  contacts,
}: {
  label: ReturnType<typeof useLiveSpeakerLabel>;
  contacts: Contact[];
}) {
  let text: string;
  let isUser = false;
  let isWaiting = false;
  if (!label) {
    text = "Listening…";
    isWaiting = true;
  } else if (label.stateKey === "user_self") {
    text = "You";
    isUser = true;
  } else if (label.contactId) {
    const contact = contacts.find((c) => c.id === label.contactId);
    text = contact ? getFullName(contact) : "Someone";
  } else {
    text = "Someone";
  }

  const baseClasses =
    "inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium";
  const colorClasses = isUser
    ? "bg-accent/15 text-accent"
    : isWaiting
      ? "bg-muted/60 text-muted-foreground/70"
      : "bg-muted text-muted-foreground";
  const dotClasses = isUser
    ? "bg-accent"
    : isWaiting
      ? "bg-muted-foreground/40"
      : "bg-muted-foreground/60";

  return (
    <span className={`${baseClasses} ${colorClasses}`}>
      <span className={`h-1.5 w-1.5 rounded-full animate-pulse ${dotClasses}`} />
      {text}
    </span>
  );
}

/**
 * Small chip showing which mic iOS is actually using. The `portType`
 * field from Swift lets us render unambiguously — built-in mics get
 * a positive "best for capturing across a room" framing, BT HFP
 * routes get a warning (narrowband audio + ear-canal placement both
 * hurt intelligibility for anyone other than the wearer). Dimmed
 * when `active` is false so the user knows it's the PLANNED route,
 * not the live one.
 */
function InputRouteChip({ route }: { route: AudioInputRoute }) {
  const isBuiltIn = route.portType === "MicrophoneBuiltIn";
  const isHeadset = !isBuiltIn;
  const opacity = route.active ? "" : "opacity-60";
  return (
    <div
      className={`flex items-start gap-2 rounded-lg border p-2.5 text-[11px] ${opacity} ${
        isHeadset
          ? "border-amber-400/40 bg-amber-400/5 text-amber-100/90"
          : "border-border/60 bg-muted/40 text-muted-foreground"
      }`}
    >
      <Headphones className="h-3.5 w-3.5 mt-0.5 shrink-0" />
      <div className="flex flex-col gap-0.5">
        <span>
          Mic: <span className="font-medium text-foreground">{route.label}</span>
          {isBuiltIn && (
            <span className="text-muted-foreground/60"> (built-in, 48 kHz)</span>
          )}
        </span>
        {isHeadset && (
          <span className="text-[10px] leading-snug text-amber-100/70">
            BT headset is narrowband (8–16 kHz) and captures distant speakers
            poorly. Built-in phone mic works better for in-person conversations.
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * One diarized transcript turn. Renders user_self in primary color,
 * known contacts with their full name, unresolved unknowns with the
 * catalog's stable "Speaker A/B" label. When the turn is NOT
 * user_self, a compact "This was me" button sits in the row footer —
 * tapping it reassigns the whole cluster to user_self and folds the
 * audio slice into the user's voiceprint.
 */
function TranscriptTurnRow({
  turn,
  contacts,
  onReassignAsSelf,
}: {
  turn: ProcessedTurn;
  contacts: Contact[];
  onReassignAsSelf: (turnId: string, clusterLocalId: number) => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [playing, setPlaying] = useState(false);
  const isUser = turn.contactId === "user_self";

  let speakerText: string;
  if (isUser) {
    speakerText = "You";
  } else if (turn.contactId) {
    const contact = contacts.find(
      (c) => c.id === turn.contactId || c.appleContactId === turn.contactId
    );
    speakerText = contact ? getFullName(contact) : "Other";
  } else if (turn.speakerLabel) {
    speakerText = turn.speakerLabel;
  } else {
    speakerText = "Other";
  }

  const handleTap = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await onReassignAsSelf(turn.id, turn.clusterLocalId);
    } finally {
      setBusy(false);
    }
  };

  const handlePlay = async () => {
    if (playing) return;
    setPlaying(true);
    try {
      await invoke("plugin:voice|play_turn_audio", {
        sessionId: turn.sessionId,
        startSeconds: turn.startSeconds,
        endSeconds: turn.endSeconds,
      });
    } catch (err) {
      console.warn("play turn audio failed:", err);
    } finally {
      setPlaying(false);
    }
  };

  return (
    <li
      className={`flex flex-col gap-1 rounded-lg border p-3 ${
        isUser ? "border-primary/40 bg-primary/5" : "border-border bg-muted/30"
      }`}
    >
      <div className="flex items-center justify-between text-xs">
        <span
          className={`font-semibold uppercase tracking-[0.2em] ${
            isUser ? "text-primary" : "text-muted-foreground"
          }`}
        >
          {speakerText}
        </span>
        <span className="font-mono text-muted-foreground">
          {formatTime(turn.startSeconds)} – {formatTime(turn.endSeconds)}
        </span>
      </div>
      {turn.transcript && (
        <p className="text-sm leading-relaxed">{turn.transcript}</p>
      )}
      <div className="flex items-center justify-between gap-2 pt-1">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handlePlay}
            disabled={playing}
            aria-label="Play this turn"
            className="inline-flex items-center gap-1 rounded-full border border-border/60 bg-muted/40 px-2 py-1 text-[11px] text-muted-foreground active:scale-95 disabled:opacity-50"
          >
            {playing ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <Play className="h-3 w-3" />
            )}
            Play
          </button>
          <span className="text-xs text-muted-foreground">
            {turn.similarity !== null
              ? `cosine ${turn.similarity.toFixed(3)}`
              : ""}
          </span>
        </div>
        {!isUser && (
          <button
            type="button"
            onClick={handleTap}
            disabled={busy}
            className="inline-flex items-center gap-1 rounded-full border border-primary/30 bg-primary/10 px-2.5 py-1 text-[11px] font-medium text-primary active:scale-95 disabled:opacity-50"
          >
            {busy ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <UserCheck className="h-3 w-3" />
            )}
            This was me
          </button>
        )}
      </div>
    </li>
  );
}

function DevResultSegment({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex items-baseline gap-1">
      <span className="text-[14px] font-semibold text-yellow-100">{value}</span>
      <span className="uppercase tracking-[0.15em] text-yellow-300/70">
        {label}
      </span>
    </div>
  );
}
